import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../../services/config.service';
import { UserService } from '../../../services/user.service';
import { BrowseService } from '../../services/browse.service';
import { RoutingService } from '../../../services/routing.service';
import { SearchService } from '../../../search/services/search.service';
import cloneDeep from 'lodash/cloneDeep';

/**
 * Main browse panel viewer component.
 */
@Component({
	selector: 'browse-view',
	templateUrl: './browse-view.component.html',
	styleUrl: './browse-view.component.css'
})
export class BrowseViewComponent implements OnInit, OnDestroy {
	private searchEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	config: any = {};
	browseObj: any = {};

	constructor(
		private browseService: BrowseService,
		private userService: UserService,
		private searchService: SearchService,
		private configService: ConfigService,
		private routingService: RoutingService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'cleanSearches') {
				if (this.browseObj) {
					this.browseObj.currentBrowse = null;
				}
			} else if (event.type === 'openBrowseLink') {
				const browsePath = event.data.link;
				this.openBrowseLink(browsePath, 0);
				
			}
		});
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
		  switch (eventId) {
	       case 'localizationLoaded':
			 this.LOCALIZATION = this.userService.LOCALIZATION;
	         break;
          }
	    });
		this.config = this.searchService.browseConfig;		
		this.loadConfiguration();
	}
	
	ngOnDestroy() {
	    if (this.searchEventSubscription) {
	        this.searchEventSubscription.unsubscribe();
	    }
	}	

	private loadConfiguration(): void {
		this.browseService.readConfigurationFile().then(
			(response: any) => {
				this.browseObj = this.browseService.config;
				if (!this.userPermissions["ROLE_PRODUCT_UPDATE"]) {
					this.browseObj.canEdit = false;
				}
				if (this.browseService.cleanOnLoad) {
					this.browseService.cleanOnLoad = false;
					this.searchService.broadcastCleanSearchesEvent();
				}

				if (this.config && this.config.browse && this.configService.getConfig().browse.enabled) {
					// Load dashboard entry point
					this.config.browse.action = this.config.action;
					this.config.browse.label = this.config.browse.browseLabel || this.config.label;
					this.config.browse.description = (!this.config.description || this.config.description.indexOf('<a ')>-1)?'':this.config.description;
					this.browseService.manualBrowseConfiguration(this.config.browse);
					this.loadBrowseEntryPoint(this.browseObj.entryPoints.length - 1);
				} else {
					let criteriaParam = this.routingService.getQueryParam('criteria') || '';
					if (criteriaParam.indexOf('initial-browse-path=') == 0) {
						let browsePath = criteriaParam.substring(20);
						let browseEntryPointIndex = parseInt(browsePath.substring(0, browsePath.indexOf('~')));
						browsePath = browsePath.substring(browsePath.indexOf('~') + 1);
						let initialBrowseNodes = browsePath.split(this.browseService.pathSeparator);
						let initialChildNodes = [];
						if (browsePath.length > 0 && browsePath != 'ROOT') {
							for (let i = 0; i < initialBrowseNodes.length; i++) {
								let tokens = initialBrowseNodes[i].split(/:(.*)/);
								let plainname = tokens[0];
								if (plainname.endsWith('_ss')) {
									plainname = plainname.substring(0, plainname.length - 3);
								} else if (plainname.endsWith('_s')) {
									plainname = plainname.substring(0, plainname.length - 2);
								}
								let childNode = {
									name: tokens[0],
									value: tokens[1],
									displayvalue: tokens[1],
									plainname: plainname,
									plainvalue: tokens[1],
									browsepath: '',
									searchpath: '',
									contents: 1
								};
								if (i == 0) {
									childNode.browsepath = plainname + ':' + tokens[1];
									childNode.searchpath = initialBrowseNodes[i];
								} else {
									childNode.browsepath = initialChildNodes[i - 1].browsepath + this.browseService.pathSeparator + plainname + ':' + tokens[1];
									childNode.searchpath = initialChildNodes[i - 1].searchpath + this.browseService.pathSeparator + initialBrowseNodes[i];
								}
								initialChildNodes.push(childNode);
							}
						} else {
							browsePath = '';
						}
						this.loadBrowseEntryPoint(browseEntryPointIndex, browsePath, initialChildNodes);
						if (initialChildNodes.length > 0) {
							this.setCurrentNode(initialChildNodes.length - 1);
						}
					}
				}
			},
			(err: any) => {
				console.error("Failed to fetch browse configuration", err);
				this.browseObj = {};
			}
		);
	}

	private openBrowseLink(browsePath: string, browseEntryPointIndex: number): void {
		let initialBrowseNodes = browsePath.split(this.browseService.pathSeparator);
		let initialChildNodes = [];
		let normBrowsePath = '';
		if (browsePath.length > 0 && browsePath != 'ROOT') {
			for (let i = 0; i < initialBrowseNodes.length; i++) {
				let tokens = initialBrowseNodes[i].split(/:(.*)/);
				let plainname = tokens[0];
				let nrmName = tokens[0];
				if (!nrmName.endsWith('_s') && !nrmName.endsWith('_ss') && nrmName.startsWith('browse')) {
					nrmName+='_ss';
				}
				if (plainname.endsWith('_ss')) {
					plainname = plainname.substring(0, plainname.length - 3);
				} else if (plainname.endsWith('_s')) {
					plainname = plainname.substring(0, plainname.length - 2);
				}
				if(i>0){
					browsePath+=this.browseService.pathSeparator + nrmName + ":" + tokens[1];
				} else {
					browsePath=nrmName+":"+tokens[1];
				}
				let childNode = {
					name: nrmName,
					value: tokens[1],
					displayvalue: tokens[1],
					plainname: plainname,
					plainvalue: tokens[1],
					browsepath: '',
					searchpath: '',
					contents: 1
				};
				if (i == 0) {
					childNode.browsepath = nrmName + ':' + tokens[1];
					childNode.searchpath = childNode.browsepath; //initialBrowseNodes[i];
				} else {
					childNode.browsepath = initialChildNodes[i - 1].browsepath + this.browseService.pathSeparator + nrmName + ':' + tokens[1];
					childNode.searchpath = childNode.browsepath; //initialChildNodes[i - 1].searchpath + this.browseService.pathSeparator + initialBrowseNodes[i];
				}
				initialChildNodes.push(childNode);
			}
		} else {
			browsePath = '';
		}
		browseEntryPointIndex = this.configService.getConfig().browse?.defaultEntyPoint?this.configService.getConfig().browse.defaultEntyPoint : browseEntryPointIndex;
		this.loadBrowseEntryPoint(browseEntryPointIndex, browsePath, initialChildNodes);
		if (initialChildNodes.length > 0) {
			//this.setCurrentNode(initialChildNodes.length - 1);
		}
	}
	
	pageChanged(newPage: number): void {
		let options = this.loadCurrentBrowseOptions();
		options.pageSize = this.browseObj.currentBrowse.pager.pageSize;
		//options.page = this.browseObj.currentBrowse.pager.page;
		options.page = newPage; // Use the actual new page number
		this.getCurrentBrowseNode(options);
	}

	loadBrowseEntryPoint(index: number, browseEntryPoint?: any, initialBrowseNodes?: any): void {
		this.searchService.broadcastCleanSearchesEvent();

		this.browseObj.currentBrowse = this.browseObj.entryPoints[index];
		this.browseObj.currentBrowse.entryIndex = index;
		this.browseObj.currentBrowse.options = this.loadCurrentBrowseOptions();
		this.browseObj.currentBrowse.pager = {
			page: this.browseObj.pagination ? 1 : null,
			pageSize: this.browseObj.pagination ? this.browseObj.pageSize : null
		};

		this.browseObj.currentBrowse.paths = {};
		let paths = [];
		if (this.browseObj.currentBrowse.path && this.browseObj.currentBrowse.path.length > 0) {
			paths = this.browseObj.currentBrowse.path.split("||");
		} else if (browseEntryPoint && paths.length == 0) {
			paths = browseEntryPoint.split(">>");
		}

		for (let i = 0; i < paths.length; i++) {
			let fields = paths[i].split(":");
			if (fields[1]) {
				this.browseObj.currentBrowse.paths[fields[0]] = fields[1];
			}
		}

		this.browseObj.currentBrowse.currentPath = "";
		this.browseObj.currentBrowse.currentLevel = -1;
		this.browseObj.currentBrowse.nodes = initialBrowseNodes ? initialBrowseNodes : [];

		Object.entries(this.browseObj.currentBrowse.paths).forEach(([key, value]) => {
			++this.browseObj.currentBrowse.currentLevel;
			if (this.browseObj.currentBrowse.currentPath) { this.browseObj.currentBrowse.currentPath += this.browseService.pathSeparator; }
			this.browseObj.currentBrowse.currentPath += key + ':' + value;
		});

		let options = this.loadCurrentBrowseOptions();
		if (this.browseObj.pagination) {
			options.page = this.browseObj.currentBrowse.pager.page;
			options.pageSize = this.browseObj.currentBrowse.pager.pageSize;
		}

		this.getCurrentBrowseNode(options);
	}

	getCurrentBrowseNode(options: any): void {
		this.browseService.getBrowseNode(this.browseObj.currentBrowse.currentPath, options).then(
			(data: any) => {
				this.browseObj.currentBrowse.pager.total = data.total;
				if(data.total>0 && data.page>0){
				  this.browseObj.currentBrowse.pager.page = data.page;
				}
				if (!this.browseObj.treeMode) {
					this.browseObj.currentBrowse.results = data.result;
					//if (this.browseObj.actionSource === "dashboard") {
					let values = this.browseObj.currentBrowse.currentPath.split(this.browseService.pathSeparator);
					this.searchBrowseFolder(values);
					//}
				} else {
					// Treemode no longer supported
					console.log("Browse treemode is not supported.");
				}
			},
			(err: any) => {
				console.error("Failed to fetch current browse node", err);
			}
		);
	}

	loadBrowseChildNode(childNode: any): void {
		this.browseObj.currentBrowse.currentNode = childNode;
		this.browseObj.currentBrowse.currentLevel = this.browseObj.currentBrowse.nodes.length;
		this.browseObj.currentBrowse.nodes.push(childNode);
		this.browseObj.currentBrowse.currentPath = childNode.searchpath;

		let field = this.browseObj.currentBrowse.structures[this.browseObj.currentBrowse.currentLevel];
		if (!field) { return; }
		let options = this.loadCurrentBrowseOptions();
		if (this.browseObj.pagination) {
			options.pageSize = this.browseObj.currentBrowse.pager.pageSize;
			options.page = 1;
		}

		this.browseService.getBrowseNode(this.browseObj.currentBrowse.currentPath, options).then(
			(data: any) => {
				if (data.result) this.browseObj.currentBrowse.results = data.result;
				this.browseObj.currentBrowse.pager.page = data.page;
				this.browseObj.currentBrowse.pager.total = data.total;
			},
			(err: any) => {
				console.error("Failed to fetch browse node", err);
			}
		);
	}

	clearCurrentNodes(): void {
		if (!this.browseObj.currentBrowse || !this.browseObj.currentBrowse.plainstructures) return;
		this.searchService.broadcastClearSearchEvent();
	}

	setCurrentNode(index: number): void {
		if (index < 0) {
			this.browseObj.currentBrowse = null;
			this.browseObj.currentBrowse = null;
			this.browseService.resetentryPoints();
			this.searchService.broadcastCleanSearchesEvent();
			return;
		}

		let value = this.browseObj.currentBrowse.nodes[index];
		let newBreadCrumb = [];
		for (let i = 0; i < index; i++) {
			newBreadCrumb.push(this.browseObj.currentBrowse.nodes[i]);
		}
		this.browseObj.currentBrowse.nodes = newBreadCrumb;
		this.searchBrowseChildNode(value);
		this.loadBrowseChildNode(value);
	}

	searchBrowseChildNode(childNode: any): void {
		this.browseObj.currentBrowse.currentNode = childNode;
		let values = childNode.browsepath.split(this.browseService.pathSeparator);
		this.searchBrowseFolder(values);
	}

	private findBrowseArrayIndexOf(values: any[], value: string): number {
		let index = values.indexOf(value);
		if (index < 0) {
			if (value.endsWith('_ss')) {
				value = value.substring(0, value.length - 3);
			} else if (value.endsWith('_s')) {
				value = value.substring(0, value.length - 2);
			} else {
				value = value + "_ss";
			}
			index = values.indexOf(value);
		}
		return index;
	}

	private fillPathWithEmptyStructure(folders: any[], browsefields: any[]): string {
		let path = "";
		let pathfields: any[] = [];
		folders.forEach((folder: any) => {
			let field = folder.split(/:(.+)/);
			pathfields.push(field[0]);
		});

		let filled: any[] = [];
		browsefields.forEach((field: any) => {
			let folder = null;
			let pathfolder = this.findBrowseArrayIndexOf(pathfields, field);
			if (pathfolder > -1) {
				let pair = folders[pathfolder].split(/:(.+)/);
				folder = { fieldname: field, fieldvalue: pair[1] };
			} else {
				if (this.browseObj.currentBrowse.showDirectChildsOnly) {
					folder = { fieldname: field, fieldvalue: "{empty}" };
				}
			}
			if (folder) {
				filled.push(folder);
			}
		});

		let filledpath = '';
		filled.forEach((folder: any) => {
			filledpath = ((filledpath) ? (filledpath + this.browseService.pathSeparator) : "") + folder.fieldname + ":" + folder.fieldvalue;
		});
		return filledpath || path;
	}

	private searchBrowseFolder(values: any[]): void {
		let facetsField = this.fillPathWithEmptyStructure(values, this.browseObj.currentBrowse.structures);

		// Set doc order from config
		let sortopts = {
			sortDirection: "DESC",
			sortField: undefined,
			libraries: '',
			keepState: false
		};
		let options = this.loadCurrentBrowseOptions();
		if (options.docsort) {
			let sortOptions = options.docsort.split(':');
			if (sortOptions.length == 2) {
				let DIR = sortOptions[1] == "asc" || sortOptions[1] == "ASC" ? "ASC" : "DESC";
				let field = sortOptions[0];

				sortopts.sortDirection = DIR;
				sortopts.sortField = field;
			}
		}

		if (options.libraries) {
			sortopts.libraries = options.libraries.split(",");
		}

		if (this.config && this.config.browse) {
			sortopts.keepState = true; // Fix issue where UI state is updated after the search
		}

		let searchInputs: any[] | null = cloneDeep(this.userService.user.searchInputs);
		this.searchService.broadcastUserTriggeredBrowseCriteriaUpdateEvent(searchInputs, sortopts, facetsField);
	}

	private loadCurrentBrowseOptions(): any {
		let options = {
			sort: this.browseObj.currentBrowse.sort || "AMT:DESC",
			docsort: this.browseObj.currentBrowse.docsort || null,
			limit: this.browseObj.maxFetched,
			structure: (this.browseObj.currentBrowse.structure.split("||").join(this.browseService.pathSeparator)),
			withempty: this.browseObj.currentBrowse.includeEmptyFolders,
			libraries: ''
		};
		if (this.browseObj.currentBrowse.libraries) {
			options.libraries = this.browseObj.currentBrowse.libraries;
		} else {
			let library = [];
			for (let i = 0; i < this.userService.user.properties.awlibraryObjectList.length; i++) {
				library.push(this.userService.user.properties.awlibraryObjectList[i].name);
			}
			options.libraries = library.join(",");
		}
		return options;
	}
}
